import Folder from "@mui/icons-material/Folder";
import { Box, Button, Card, Chip, Link, Paper, Skeleton, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { GradientIcon } from "../../generic/components/gradient-icon";
import { ProgramIcon } from "../../programs/components/program-icon";
import { useRecentPrograms } from "../hooks/use-recent-programs";

import type { FC } from "react";

/** The programs most recently created Communications in, as a way back into them. */
export const RecentProgramsCard: FC = () => {
  const { data: programs, error } = useRecentPrograms();

  if (error) return null;

  return (
    <Card
      sx={{
        p: 2,
        height: "100%",
        boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Paper
          elevation={2}
          sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75 }}
        >
          <GradientIcon>
            <Folder sx={{ fontSize: 18 }} />
          </GradientIcon>
          <Typography variant="subtitle2">Recent Programs</Typography>
        </Paper>
        {Boolean(programs?.length) && (
          <Link component={RouterLink} to="/tabbed-programs" variant="subtitle2" underline="hover">
            View all
          </Link>
        )}
      </Box>

      {programs === null ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {[160, 140, 120].map((width) => (
            <Skeleton key={width} variant="rounded" width={width} height={32} />
          ))}
        </Box>
      ) : programs.length === 0 ? (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" sx={{ textAlign: "left", mb: 3 }}>
            Programs enable you to organise your Communications into groups. Your most recently used
            Programs will appear here!
          </Typography>
          <Button
            variant="contained"
            href="/moments"
            data-analytics-id="dashboard-recent-programs-create"
          >
            Create your first Communication
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {programs.map(({ id, title, colour, icon }) => (
            <Chip
              key={id}
              component={RouterLink}
              to={`/tabbed-programs/${id}`}
              clickable
              color="primary"
              variant="outlined"
              icon={
                // ProgramIcon's Tooltip drops the class Chip gives its icon, so a span takes it.
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <ProgramIcon data={icon ?? undefined} fontSize="small" sx={{ color: colour }} />
                </Box>
              }
              label={title}
              // MUI's outlined chip tucks its icon in close to the border; give it room.
              sx={{ "& .MuiChip-icon": { ml: 1, mr: -0.25 } }}
            />
          ))}
        </Box>
      )}
    </Card>
  );
};
